import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Sparkle,
  Lightning,
  CheckCircle,
  Crown,
  Receipt,
  ShieldCheck,
  ArrowLeft,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { setPendingUploads } from "../lib/uploadStore";
import { toast } from "sonner";

const CREATOR_PRICE = { base: 199, gst: 36, total: 235 };

const PLANS = [
  {
    id: "free",
    title: "Start Free",
    price: "₹0",
    sub: "2 LUTs / day",
    perks: ["No credit card", "Instant access", "Rule-based color science"],
    accent: false,
  },
  {
    id: "creator",
    title: "Get Creator",
    price: "₹235 / mo",
    sub: "Unlimited + 60 AI credits",
    perks: [
      "Unlimited rule-based LUTs",
      "AI prompt mode (60 credits)",
      "Commercial license + GST invoice",
    ],
    accent: true,
  },
];

/**
 * AuthGateModal — combined signup + (optional) Creator checkout in one popup.
 *
 *  - "free" plan: signup → onSuccess('free') → caller continues to Studio.
 *  - "creator" plan: signup → billing step → POST /payments/create → launch
 *    Cashfree right here. Pending uploads (if any) are persisted to IndexedDB
 *    before the redirect so they survive the round-trip.
 */
export default function AuthGateModal({
  open,
  onOpenChange,
  onSuccess,
  pendingVideo = null,
  pendingReference = null,
  pendingPrompt = "",
}) {
  const { signup, user } = useAuth();
  const [step, setStep] = useState("auth"); // 'auth' | 'billing' | 'redirecting'
  const [mode, setMode] = useState("free"); // 'free' | 'creator'
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [billing, setBilling] = useState({
    full_name: "",
    phone: "",
    gstin: "",
    address: "",
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  // Reset internal state when modal opens
  useEffect(() => {
    if (!open) return;
    setStep("auth");
    setLoading(false);
    if (!user) return;
    setName(user.name || "");
    setEmail(user.email || "");
    setBilling((b) => ({
      ...b,
      full_name: b.full_name || user.name || "",
    }));
  }, [open, user]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const billingErrors = useMemo(() => {
    const e = {};
    if (!billing.full_name.trim()) e.full_name = "Required";
    if (!billing.phone.trim() || billing.phone.replace(/\D/g, "").length < 7)
      e.phone = "Valid phone required";
    if (!billing.address.trim() || billing.address.trim().length < 4)
      e.address = "Billing address required";
    if (
      billing.gstin &&
      !/^[0-9A-Z]{15}$/.test(billing.gstin.trim().toUpperCase())
    )
      e.gstin = "GSTIN must be 15 alphanumeric characters";
    return e;
  }, [billing]);

  const billingValid = Object.keys(billingErrors).length === 0;

  // === Step 1: sign up (or use existing session) ===
  const submitAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!user) {
        await signup(email, password, name);
      }
      // Pre-fill billing with what we now know
      setBilling((b) => ({
        ...b,
        full_name: b.full_name || name || user?.name || "",
      }));
      if (mode === "creator") {
        setStep("billing");
        setLoading(false);
        return;
      }
      // Free path — close + bubble up
      toast.success("Account created — let's grade");
      onOpenChange(false);
      if (onSuccess) onSuccess("free");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      if (mode === "free") setLoading(false);
    }
  };

  // === Step 2 (creator only): billing → Cashfree ===
  const launchCheckout = async (data) => {
    if (data.gateway === "cashfree" && data.payment_session_id) {
      try {
        const { loadCashfreeSDK } = await import("../lib/cashfreeLoader");
        await loadCashfreeSDK();
      } catch {
        toast.error("Cashfree SDK failed to load. Please refresh and try again.");
        setStep("billing");
        return;
      }
      try {
        const cashfree = window.Cashfree({
          mode: data.cashfree_env || "sandbox",
        });
        await cashfree.checkout({
          paymentSessionId: data.payment_session_id,
          redirectTarget: "_self",
        });
      } catch (e) {
        toast.error(e?.message || "Cashfree checkout failed");
        setStep("billing");
      }
      return;
    }
    if (data.redirect_url) {
      window.location.href = data.redirect_url;
      return;
    }
    toast.error("Payment gateway returned no session. Please retry.");
    setStep("billing");
  };

  const submitBilling = async () => {
    if (!billingValid) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setLoading(true);
    setStep("redirecting");
    try {
      // Persist uploads BEFORE we redirect away from the SPA so the user
      // continues with the same files post-payment.
      if (pendingVideo && pendingReference) {
        await setPendingUploads(pendingVideo, pendingReference, pendingPrompt);
      }
      const payload = {
        plan: "creator",
        billing: {
          full_name: billing.full_name.trim(),
          email: (email || user?.email || "").trim().toLowerCase(),
          phone: billing.phone.trim(),
          gstin: billing.gstin ? billing.gstin.trim().toUpperCase() : null,
          address: billing.address.trim(),
        },
      };
      const { data } = await api.post("/payments/create", payload);
      await launchCheckout(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Unable to start payment";
      toast.error(typeof msg === "string" ? msg : "Unable to start payment");
      setStep("billing");
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = PLANS.find((p) => p.id === mode);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="auth-gate-modal"
        className="bg-[#0b0b0b] border border-white/10 text-white max-w-2xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto"
      >
        {step === "auth" && (
          <>
            <div className="p-6 border-b border-white/5">
              <DialogHeader>
                <DialogTitle className="text-2xl tracking-tight">
                  Create your account to generate
                </DialogTitle>
                <DialogDescription className="text-zinc-500">
                  Pick a plan — you can change anytime.
                </DialogDescription>
              </DialogHeader>
            </div>

            <div className="p-6 space-y-5">
              {/* Plan selector */}
              <div className="grid grid-cols-2 gap-3">
                {PLANS.map((p) => {
                  const active = mode === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`auth-plan-${p.id}`}
                      onClick={() => setMode(p.id)}
                      className={`text-left rounded-xl p-4 border transition ${
                        active
                          ? p.accent
                            ? "border-red-500/70 bg-gradient-to-br from-red-900/30 to-black ring-2 ring-red-500/30"
                            : "border-white/30 bg-white/[0.04]"
                          : "border-white/10 bg-white/[0.02] hover:border-white/20"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {p.accent ? (
                          <Sparkle
                            size={14}
                            weight="fill"
                            className="text-red-400"
                          />
                        ) : (
                          <Lightning
                            size={14}
                            weight="fill"
                            className="text-zinc-400"
                          />
                        )}
                        <span className="text-xs font-mono uppercase tracking-widest text-zinc-300">
                          {p.title}
                        </span>
                      </div>
                      <div className="text-xl font-semibold tracking-tighter">
                        {p.price}
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">
                        {p.sub}
                      </div>
                    </button>
                  );
                })}
              </div>

              <ul className="space-y-1.5 text-xs text-zinc-400">
                {selectedPlan.perks.map((perk, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckCircle size={12} className="text-red-500" />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>

              {/* Signup form (skipped if user already authed) */}
              <form
                onSubmit={submitAuth}
                className="space-y-3"
                data-testid="auth-gate-form"
              >
                {!user && (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                        Name
                      </Label>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        data-testid="auth-gate-name"
                        placeholder="Your name"
                        className="bg-white/[0.03] border-white/10 text-white"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                          Email
                        </Label>
                        <Input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          data-testid="auth-gate-email"
                          className="bg-white/[0.03] border-white/10 text-white"
                          placeholder="you@studio.com"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                          Password
                        </Label>
                        <Input
                          type="password"
                          required
                          minLength={6}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          data-testid="auth-gate-password"
                          className="bg-white/[0.03] border-white/10 text-white"
                          placeholder="••••••••"
                        />
                      </div>
                    </div>
                  </>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="auth-gate-submit"
                  className="w-full bg-red-600 hover:bg-red-500 mt-1 rounded-lg shadow-[0_0_28px_rgba(255,77,57,0.4)] hover:shadow-[0_0_40px_rgba(255,77,57,0.6)]"
                >
                  {loading
                    ? user
                      ? "Loading…"
                      : "Creating account…"
                    : mode === "creator"
                    ? user
                      ? "Continue to billing →"
                      : "Create account & continue to checkout"
                    : user
                    ? "Continue"
                    : "Create account & generate"}
                </Button>
                {!user && (
                  <p className="text-xs text-zinc-500 text-center">
                    Already have an account?{" "}
                    <a
                      href="/login"
                      className="text-red-400 hover:underline"
                      data-testid="auth-gate-login-link"
                    >
                      Sign in
                    </a>
                  </p>
                )}
              </form>
            </div>
          </>
        )}

        {step === "billing" && (
          <>
            <div className="bg-gradient-to-br from-red-900/30 via-zinc-950 to-black border-b border-red-500/20 px-6 py-5">
              <DialogHeader>
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-red-400 mb-2">
                  <Crown size={12} weight="fill" />
                  Step 2 of 2 — Billing details
                </div>
                <DialogTitle className="text-2xl font-medium tracking-tighter text-white">
                  Unlock unlimited cinematic LUTs
                </DialogTitle>
                <DialogDescription className="text-zinc-400 text-sm">
                  30-day Creator subscription · 60 AI credits · GST tax invoice
                  emailed instantly.
                </DialogDescription>
              </DialogHeader>
            </div>

            <div className="px-6 py-5 grid md:grid-cols-5 gap-6">
              {/* LEFT — pricing summary */}
              <div className="md:col-span-2 space-y-5">
                <div
                  data-testid="auth-gate-price-box"
                  className="rounded-xl border border-red-500/30 bg-gradient-to-br from-red-900/20 to-black p-4"
                >
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-red-400 mb-2">
                    Creator · Monthly
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <Row
                      label="Base price"
                      value={`₹${CREATOR_PRICE.base.toFixed(2)}`}
                    />
                    <Row
                      label="GST @ 18%"
                      value={`₹${CREATOR_PRICE.gst.toFixed(2)}`}
                      sub="(CGST 9% + SGST 9%)"
                    />
                  </div>
                  <div className="border-t border-red-500/25 mt-3 pt-3 flex items-center justify-between">
                    <span className="text-xs font-mono uppercase tracking-wider text-zinc-300">
                      Total payable
                    </span>
                    <span
                      data-testid="auth-gate-total"
                      className="text-3xl font-semibold tracking-tighter text-red-400"
                    >
                      ₹{CREATOR_PRICE.total.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500 mt-2">
                    Renews every 30 days · Cancel anytime
                  </div>
                </div>

                <ul className="space-y-2.5 text-[13px] text-zinc-300">
                  <Perk icon={<Sparkle size={14} weight="fill" />}>
                    Unlimited rule-based LUTs
                  </Perk>
                  <Perk icon={<Sparkle size={14} weight="fill" />}>
                    60 AI credits (≈ 6 AI LUTs)
                  </Perk>
                  <Perk icon={<Receipt size={14} weight="fill" />}>
                    GST tax invoice (PDF) emailed
                  </Perk>
                  <Perk icon={<ShieldCheck size={14} weight="fill" />}>
                    Commercial usage license
                  </Perk>
                </ul>
              </div>

              {/* RIGHT — billing details */}
              <div className="md:col-span-3 space-y-3">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400 mb-1">
                  Billing details
                </div>

                <Field
                  testId="auth-gate-billing-name"
                  label="Full name"
                  value={billing.full_name}
                  onChange={(e) =>
                    setBilling((b) => ({ ...b, full_name: e.target.value }))
                  }
                  error={billingErrors.full_name}
                  required
                />
                <Field
                  testId="auth-gate-billing-email"
                  label="Email"
                  type="email"
                  value={email || user?.email || ""}
                  readOnly
                  hint="Linked to your account"
                />
                <Field
                  testId="auth-gate-billing-phone"
                  label="Phone"
                  value={billing.phone}
                  onChange={(e) =>
                    setBilling((b) => ({ ...b, phone: e.target.value }))
                  }
                  error={billingErrors.phone}
                  required
                  placeholder="9876543210"
                />
                <Field
                  testId="auth-gate-billing-gstin"
                  label="GSTIN (optional, for businesses)"
                  value={billing.gstin}
                  onChange={(e) =>
                    setBilling((b) => ({
                      ...b,
                      gstin: e.target.value.toUpperCase(),
                    }))
                  }
                  error={billingErrors.gstin}
                  placeholder="22AAAAA0000A1Z5"
                  maxLength={15}
                />
                <div>
                  <Label
                    htmlFor="auth-gate-billing-address"
                    className="text-[11px] font-mono uppercase tracking-wider text-zinc-400"
                  >
                    Billing address <span className="text-red-400">*</span>
                  </Label>
                  <textarea
                    id="auth-gate-billing-address"
                    data-testid="auth-gate-billing-address"
                    rows={3}
                    placeholder="Street, City, State, PIN"
                    value={billing.address}
                    onChange={(e) =>
                      setBilling((b) => ({ ...b, address: e.target.value }))
                    }
                    className={`mt-1 w-full rounded-md bg-white/[0.03] border ${
                      billingErrors.address
                        ? "border-red-500/60"
                        : "border-white/10"
                    } text-sm text-zinc-100 px-3 py-2 focus:outline-none focus:border-red-500/40 resize-none`}
                  />
                  {billingErrors.address && (
                    <div className="text-[10px] text-red-400 mt-1">
                      {billingErrors.address}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-white/8 px-6 py-4 flex items-center justify-between gap-3 bg-black/40">
              <Button
                variant="ghost"
                onClick={() => setStep("auth")}
                disabled={loading}
                data-testid="auth-gate-billing-back"
                className="text-zinc-400 hover:text-white"
              >
                <ArrowLeft size={14} className="mr-1.5" /> Back
              </Button>
              <Button
                data-testid="auth-gate-billing-pay"
                onClick={submitBilling}
                disabled={loading || !billingValid}
                className="bg-red-600 hover:bg-red-500 shadow-[0_0_24px_rgba(255,77,57,0.4)]"
              >
                {loading
                  ? "Redirecting…"
                  : `Pay ₹${CREATOR_PRICE.total.toFixed(0)} via Cashfree`}
              </Button>
            </div>
          </>
        )}

        {step === "redirecting" && (
          <div className="p-12 text-center" data-testid="auth-gate-redirecting">
            <div className="inline-block w-10 h-10 rounded-full border-4 border-red-500/30 border-t-red-500 animate-spin mb-4" />
            <h3 className="text-lg font-medium text-white mb-1">
              Opening Cashfree checkout…
            </h3>
            <p className="text-xs text-zinc-500">
              You&apos;ll be redirected in a moment.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, sub }) {
  return (
    <div className="flex items-center justify-between text-zinc-300">
      <span className="text-zinc-400">
        {label}
        {sub && <span className="text-[10px] text-zinc-600 ml-1">{sub}</span>}
      </span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

function Perk({ icon, children }) {
  return (
    <li className="flex items-start gap-2">
      <span className="text-red-500 mt-0.5">{icon}</span>
      <span>{children}</span>
    </li>
  );
}

function Field({
  testId,
  label,
  value,
  onChange,
  type = "text",
  error,
  required,
  placeholder,
  readOnly,
  maxLength,
  hint,
}) {
  return (
    <div>
      <Label className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">
        {label} {required && <span className="text-red-400">*</span>}
      </Label>
      <Input
        data-testid={testId}
        value={value}
        onChange={onChange}
        type={type}
        placeholder={placeholder}
        readOnly={readOnly}
        maxLength={maxLength}
        className={`mt-1 bg-white/[0.03] border text-sm h-9 ${
          error ? "border-red-500/60" : "border-white/10"
        } ${readOnly ? "opacity-70 cursor-not-allowed" : ""}`}
      />
      {error && <div className="text-[10px] text-red-400 mt-1">{error}</div>}
      {!error && hint && (
        <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>
      )}
    </div>
  );
}
